/**
 * AdmitadIntegrationPage — connection form + sync-status panel for the
 * Admitad ingest partner. Light theme, GREEN accent only.
 *
 * Polymorphic copy rule: the exact partner name "Admitad" appears once, in
 * the page title. Everywhere else uses the neutral label "Partner Network".
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	isValidApiKey,
	isValidPublisherId,
	type CredentialRow,
} from "./api";
import {
	useAdmitadCredentialCreate,
	useAdmitadCredentialsList,
	useAdmitadSyncStatus,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const INPUT_INVALID =
	"w-full rounded-[6px] border border-red-400 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

function formatTimestamp(epochSec: number): string {
	if (!epochSec || Number.isNaN(epochSec)) return "never";
	const d = new Date(epochSec * 1000);
	if (Number.isNaN(d.getTime())) return "never";
	return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

function lastSyncFromCreds(items: CredentialRow[] | undefined): number {
	if (!items || items.length === 0) return 0;
	return items.reduce((max, r) => (r.created_at > max ? r.created_at : max), 0);
}

export function AdmitadIntegrationPage() {
	const credsQ = useAdmitadCredentialsList();
	const statusQ = useAdmitadSyncStatus();
	const createMut = useAdmitadCredentialCreate();

	const [publisherId, setPublisherId] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [saved, setSaved] = useState(false);

	const publisherIdValid = publisherId.length === 0 || isValidPublisherId(publisherId);
	const apiKeyValid = apiKey.length === 0 || isValidApiKey(apiKey);
	const canSubmit =
		publisherId.length > 0 &&
		apiKey.length > 0 &&
		publisherIdValid &&
		apiKeyValid &&
		!createMut.isPending;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		if (!canSubmit) return;
		createMut.mutate(
			{
				admitad_client_id: publisherId.trim(),
				admitad_api_key: apiKey.trim(),
			},
			{
				onSuccess: () => {
					setSaved(true);
					setApiKey("");
				},
			},
		);
	};

	const lastSyncEpoch = useMemo(
		() => lastSyncFromCreds(credsQ.data?.items),
		[credsQ.data],
	);

	const connected = Boolean(statusQ.data?.has_credentials);
	const hasPostbacks = Boolean(statusQ.data?.has_postbacks);

	return (
		<main
			data-testid="admitad-integration-page"
			className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-3xl font-semibold text-transparent">
					Admitad Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Connect your Partner Network account to ingest conversion postbacks
					and map offers to Trackerity.
				</p>
			</header>

			{/* Sync status panel */}
			<section
				className={CARD}
				data-testid="admitad-integration-status"
				aria-label="sync status"
			>
				<div className="flex flex-col gap-3">
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-zinc-800">
							Partner Network Status
						</h2>
						<span
							className={
								connected
									? "rounded-full border border-green-300 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-800"
									: "rounded-full border border-zinc-300 bg-zinc-50 px-2.5 py-0.5 text-xs font-medium text-zinc-700"
							}
							data-testid="admitad-integration-status-badge"
						>
							{statusQ.isLoading
								? "checking"
								: connected
									? "Connected"
									: "Not connected"}
						</span>
					</div>
					<dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
						<div>
							<dt className="text-xs uppercase tracking-wide text-zinc-500">
								Credentials
							</dt>
							<dd
								className="text-sm text-zinc-900"
								data-testid="admitad-integration-status-creds"
							>
								{statusQ.data?.has_credentials ? "Configured" : "Missing"}
							</dd>
						</div>
						<div>
							<dt className="text-xs uppercase tracking-wide text-zinc-500">
								Offer Mappings
							</dt>
							<dd className="text-sm text-zinc-900">
								{statusQ.data?.has_offer_mappings ? "Configured" : "None"}
							</dd>
						</div>
						<div>
							<dt className="text-xs uppercase tracking-wide text-zinc-500">
								Recent Postbacks
							</dt>
							<dd className="text-sm text-zinc-900">
								{hasPostbacks ? "Receiving" : "None yet"}
							</dd>
						</div>
					</dl>
					<div className="border-t border-zinc-100 pt-3">
						<span className="text-xs uppercase tracking-wide text-zinc-500">
							Last Sync
						</span>
						<p
							className="text-sm text-zinc-900"
							data-testid="admitad-integration-last-sync"
						>
							{credsQ.isLoading
								? "loading"
								: lastSyncEpoch
									? formatTimestamp(lastSyncEpoch)
									: "never"}
						</p>
					</div>
					{statusQ.isLoading ? (
						<p
							className="text-xs text-zinc-500"
							data-testid="admitad-integration-loading"
							role="status"
						>
							Loading status.
						</p>
					) : null}
				</div>
			</section>

			{/* Connection form */}
			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-4`}
				data-testid="admitad-integration-form"
				aria-label="partner network connection form"
			>
				<div className="flex flex-col gap-1">
					<h2 className="text-sm font-semibold text-zinc-800">
						Add Partner Network Credentials
					</h2>
					<p className="text-xs text-zinc-500">
						Your API key is hashed before storage. Only the first few
						characters are retained for display.
					</p>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="admitad-publisher-id" className={LABEL}>
						Publisher ID
					</label>
					<input
						id="admitad-publisher-id"
						type="text"
						required
						className={publisherIdValid ? INPUT : INPUT_INVALID}
						value={publisherId}
						autoComplete="off"
						onChange={(e) => setPublisherId(e.currentTarget.value)}
						data-testid="admitad-integration-publisher-input"
						aria-invalid={!publisherIdValid}
					/>
					{!publisherIdValid ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="admitad-integration-publisher-error"
						>
							Publisher ID must be alphanumeric (dash / underscore allowed).
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="admitad-api-key" className={LABEL}>
						API Key
					</label>
					<input
						id="admitad-api-key"
						type="password"
						required
						className={apiKeyValid ? INPUT : INPUT_INVALID}
						value={apiKey}
						autoComplete="off"
						onChange={(e) => setApiKey(e.currentTarget.value)}
						data-testid="admitad-integration-key-input"
						aria-invalid={!apiKeyValid}
					/>
					{!apiKeyValid ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="admitad-integration-key-error"
						>
							API key must be at least 16 characters, alphanumeric only.
						</p>
					) : (
						<p className="text-xs text-zinc-500">
							At least 16 characters. Alphanumeric, dash, underscore.
						</p>
					)}
				</div>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN}
						disabled={!canSubmit}
						data-testid="admitad-integration-save"
					>
						{createMut.isPending ? "Saving." : "Connect Partner Network"}
					</button>
					{saved ? (
						<p
							className="text-sm text-green-700"
							role="status"
							data-testid="admitad-integration-saved"
						>
							Credentials saved.
						</p>
					) : null}
					{createMut.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="admitad-integration-error"
						>
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>
		</main>
	);
}

export default AdmitadIntegrationPage;
